// src/styles/GlobalStyles.ts
import { createGlobalStyle } from "styled-components";
import { theme } from "./theme";

export const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Poppins:wght@300;400;500;600;700;800;900&family=Fira+Code:wght@400;500;600&display=swap');
    * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  *::before,
  *::after {
    box-sizing: border-box;
  }
  
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
  
  html {
    scroll-behavior: smooth;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
  
  body {
    font-family: ${theme.fonts.main};
    font-size: ${theme.fontSizes.base};
    font-weight: ${theme.fontWeights.normal};
    line-height: ${theme.lineHeights.normal};
    background: ${theme.colors.backgroundGradient};
    background-attachment: fixed;
    color: ${theme.colors.text};
    min-height: 100vh;
    overflow-x: hidden;
    position: relative;
  }
  
  /* Melhor controle de foco para acessibilidade */
  :focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${theme.borderRadius.sm};
  }
  
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    transition: color ${theme.transitions.fast};
    
    &:hover {
      color: ${theme.colors.primaryLight};
    }
    
    &:active {
      color: ${theme.colors.primaryDark};
    }
  }
  
  button {
    cursor: pointer;
    font-family: ${theme.fonts.main};
    border: none;
    background: none;
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
  
  input, textarea, select {
    font-family: ${theme.fonts.main};
  }
  
  .container {
    max-width: ${theme.sizes.maxWidth};
    margin: 0 auto;
    padding: 0 ${theme.spacing.xl};
    
    @media (max-width: ${theme.breakpoints.md}) {
      padding: 0 ${theme.spacing.lg};
    }
    
    @media (max-width: ${theme.breakpoints.sm}) {
      padding: 0 ${theme.spacing.md};
    }
  }
  
  section {
    padding: ${theme.spacing["5xl"]} 0;
    
    @media (max-width: ${theme.breakpoints.md}) {
      padding: ${theme.spacing["4xl"]} 0;
    }
    
    @media (max-width: ${theme.breakpoints.sm}) {
      padding: ${theme.spacing["3xl"]} 0;
    }
  }
  
  /* Tipografia melhorada com hierarquia visual clara */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fonts.heading};
    font-weight: ${theme.fontWeights.bold};
    line-height: ${theme.lineHeights.tight};
    letter-spacing: -0.02em;
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.text};
  }
  
  h1 {
    font-size: ${theme.fontSizes["5xl"]};
    font-weight: ${theme.fontWeights.extrabold};
    background: linear-gradient(135deg, ${theme.colors.text} 0%, ${theme.colors.primaryLight} 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    
    @media (max-width: ${theme.breakpoints.lg}) {
      font-size: ${theme.fontSizes["4xl"]};
    }
    
    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.fontSizes["3xl"]};
    }
    
    @media (max-width: ${theme.breakpoints.sm}) {
      font-size: ${theme.fontSizes["2xl"]};
    }
  }
  
  h2 {
    font-size: ${theme.fontSizes["4xl"]};
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.primaryLight};
    position: relative;
    display: inline-block;
    
    &::after {
      content: "";
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 60px;
      height: 4px;
      background: ${theme.colors.accentGradient};
      border-radius: ${theme.borderRadius.full};
    }
    
    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.fontSizes["3xl"]};
    }
    
    @media (max-width: ${theme.breakpoints.sm}) {
      font-size: ${theme.fontSizes["2xl"]};
    }
  }
  
  h3 {
    font-size: ${theme.fontSizes["2xl"]};
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.text};
    
    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.fontSizes.xl};
    }
  }
  
  h4 {
    font-size: ${theme.fontSizes.xl};
    font-weight: ${theme.fontWeights.semibold};
    
    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.fontSizes.lg};
    }
  }
  
  p {
    margin-bottom: ${theme.spacing.lg};
    line-height: ${theme.lineHeights.relaxed};
    color: ${theme.colors.textSecondary};
    font-size: ${theme.fontSizes.base};
    
    &.lead {
      font-size: ${theme.fontSizes.lg};
      line-height: ${theme.lineHeights.relaxed};
      color: ${theme.colors.text};
    }
    
    @media (max-width: ${theme.breakpoints.sm}) {
      font-size: ${theme.fontSizes.sm};
    }
  }
  
  strong, b {
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.text};
  }
  
  code {
    font-family: ${theme.fonts.mono};
    font-size: 0.9em;
    padding: 0.2em 0.4em;
    background: rgba(30, 144, 255, 0.1);
    border-radius: ${theme.borderRadius.sm};
    color: ${theme.colors.primaryLight};
  }
  
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Animações melhoradas e mais suaves */
  @keyframes fadeIn {
    from { 
      opacity: 0;
    }
    to { 
      opacity: 1;
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(40px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-40px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideLeft {
    from {
      opacity: 0;
      transform: translateX(40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideRight {
    from {
      opacity: 0;
      transform: translateX(-40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
  }
  
  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 20px rgba(30, 144, 255, 0.4);
    }
    50% {
      box-shadow: 0 0 30px rgba(30, 144, 255, 0.7), 0 0 40px rgba(30, 144, 255, 0.5);
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-15px);
    }
  }
  
  @keyframes rotateGlow {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  
  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
  
  /* Classes de utilidade para animações */
  .animate-fadeIn {
    animation: fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  .animate-slideUp {
    animation: slideUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  .animate-slideDown {
    animation: slideDown 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  .animate-slideLeft {
    animation: slideLeft 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  .animate-slideRight {
    animation: slideRight 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  .animate-float {
    animation: float 6s ease-in-out infinite;
  }
  
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  
  .animate-glow {
    animation: glow 3s ease-in-out infinite;
  }
  
  .animate-scaleIn {
    animation: scaleIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  /* Classes de utilidade */
  .text-gradient {
    background: ${theme.colors.accentGradient};
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  .glass-effect {
    background: rgba(17, 24, 39, 0.7);
    backdrop-filter: blur(12px);
    border: 1px solid ${theme.colors.cardBorder};
  }
  
  /* Scrollbar personalizada */
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${theme.colors.background};
  }
  
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, ${theme.colors.primary}, ${theme.colors.secondary});
    border-radius: ${theme.borderRadius.full};
    
    &:hover {
      background: linear-gradient(180deg, ${theme.colors.primaryLight}, ${theme.colors.secondaryLight});
    }
  }
  
  /* Selection styling */
  ::selection {
    background: ${theme.colors.primary};
    color: ${theme.colors.text};
  }
  
  ::-moz-selection {
    background: ${theme.colors.primary};
    color: ${theme.colors.text};
  }
`;
